import { Modal, Alert } from 'antd';
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  isValidElement,
} from 'react';

import FormSubmitControls from 'components/FormSubmitControls';

/**
 * Hook para confirmar acciones mediante un modal controlado por promesa.
 *
 * API:
 * - Entrada:
 *   - @param {string | ReactNode} title — Título del modal.
 *   - @param {string | ReactNode} message — Contenido principal del modal.
 *   - @param {object} options — Opciones del modal.
 *     - @param {string | React.ReactElement} options.alert — Contenido de la alerta.
 *       alert?: string | React.ReactElement
 *       // Si se proporciona, muestra una alerta (por defecto de tipo warning) debajo del mensaje.
 *     }
 * - Salida: [ConfirmModal, confirm]
 *   - ConfirmModal: Componente que debe renderizarse en el árbol para que el modal funcione.
 *   - confirm: () => Promise<boolean> — Resuelve true si se confirma, false si se cancela.
 */
const useConfirm = (title, message, options = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const resolverRef = useRef(null);
  const { alert: alertContent } = options || {};

  const confirm = useCallback(() => new Promise((resolve) => {
    resolverRef.current = resolve;
    setIsOpen(true);
  }), []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resolverRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    if (!resolverRef.current) return;
    resolverRef.current(true);
    handleClose();
  }, [handleClose]);

  const handleCancel = useCallback(() => {
    if (!resolverRef.current) return;
    resolverRef.current(false);
    handleClose();
  }, [handleClose]);

  const ConfirmModal = () => {
    const alertNode = useMemo(() => {
      if (!alertContent) return null;
      if (isValidElement(alertContent)) {
        return (
          <div style={{ marginTop: 12 }}>
            {alertContent}
          </div>
        );
      }
      const messageText = typeof alertContent === 'string'
        ? alertContent
        : String(alertContent);
      return (
        <div style={{ marginTop: 12 }}>
          <Alert message={messageText} type="warning" showIcon />
        </div>
      );
    }, [alertContent]);

    return (
      <Modal
        title={(
          <FormSubmitControls
            label={title}
            onCancel={handleCancel}
            onFinish={handleConfirm}
          />
        )}
        open={isOpen}
        onCancel={handleCancel}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
      >
        {message}
        {alertNode}
      </Modal>
    );
  };

  return [ConfirmModal, confirm];
};

export default useConfirm;
